/**
 * What Lucas accepts as an attachment.
 *
 * The list is the **intersection of what both providers can read**, not the
 * union. A file may be routed to either model — Grok for business reasoning,
 * Kimi for code/flow/log analysis — so accepting something only one of them
 * understands produces an attachment that works sometimes.
 *
 * Provider facts (checked 2026-08-06):
 *
 * | | Kimi / Moonshot | xAI / Grok |
 * |---|---|---|
 * | Per file | 100 MB | 48 MB |
 * | Docs | pdf, doc(x), xls(x), ppt(x), txt, md, csv, html, json, epub, mobi | txt, md, csv, json, pdf + code |
 * | Code/config | broad (go, c, java, js, ts, py, yaml, ini, conf, log…) | "code files" |
 * | Vision | png, jpeg, webp, gif | **jpeg and png only**, ≤20 MiB |
 *
 * Sources: https://platform.kimi.ai/docs/api/files-upload and
 * https://docs.x.ai/developers/files
 *
 * ⚠️ Accepting a file is not the same as Lucas being able to read it. Today the
 * upload lands in S3 and the agent receives a URL; neither provider can fetch
 * our bucket. Making the content reachable means uploading to the provider's
 * own Files API (Kimi `purpose=file-extract`, xAI Files) or extracting the text
 * server-side — that lands with the Kimi/Grok integration, not here.
 */

/** Hard ceiling imposed by the narrower provider (Grok). */
export const PROVIDER_MAX_BYTES = 48 * 1024 * 1024;

/**
 * What the platform accepts on upload.
 *
 * Below the provider ceiling on purpose: the support upload buffers the whole
 * file in the API process (`multer.memoryStorage`), and the chat limiter allows
 * 15 requests per minute per user. Raising this to the provider ceiling means
 * moving to a presigned direct-to-S3 upload first, the same way the CMS does.
 */
export const MAX_UPLOAD_BYTES = 32 * 1024 * 1024;

/** Grok's vision path is far narrower than its document path. */
export const VISION_MIME_TYPES = new Set(["image/jpeg", "image/png"]);

/**
 * Accepted extensions, grouped by what they are.
 *
 * Extension-first because the browser's MIME type is unreliable: Windows sends
 * `application/octet-stream` for plenty of legitimate files, and rejecting on
 * that alone would turn away real documents.
 */
export const ATTACHMENT_EXTENSIONS = Object.freeze({
  documents: [
    ".pdf", ".doc", ".docx", ".odt", ".rtf",
    ".xls", ".xlsx", ".ods",
    ".ppt", ".pptx", ".odp",
    ".epub", ".mobi",
  ],
  text: [".txt", ".md", ".csv", ".tsv", ".log", ".html", ".htm", ".xml"],
  data: [".json", ".yaml", ".yml", ".ini", ".conf", ".toml", ".env"],
  code: [
    ".js", ".mjs", ".cjs", ".ts", ".tsx", ".jsx",
    ".py", ".py3", ".rb", ".go", ".rs", ".php",
    ".java", ".kt", ".cs", ".c", ".h", ".cpp", ".cxx", ".cc", ".hpp",
    ".sql", ".css", ".scss", ".jsp", ".asp",
  ],
  images: [".png", ".jpg", ".jpeg", ".webp", ".gif", ".bmp", ".tif", ".tiff", ".avif", ".svg"],
});

const ALLOWED_EXTENSIONS = new Set(Object.values(ATTACHMENT_EXTENSIONS).flat());

/**
 * MIME prefixes accepted regardless of extension. `text/*` covers the long tail
 * of log and source formats both providers read as plain text.
 */
const ALLOWED_MIME_PREFIXES = ["text/", "image/"];

/** Exact MIME types that aren't `text/*` or `image/*`. */
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/json",
  "application/xml",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  "application/rtf",
  "application/epub+zip",
  "application/x-yaml",
  "application/yaml",
  "application/sql",
]);

/** `accept` attribute for the file input — must not be wider than the API. */
export const ATTACHMENT_ACCEPT = [...ALLOWED_EXTENSIONS].join(",");

function extensionOf(filename) {
  const dot = String(filename || "").lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot).toLowerCase();
}

/**
 * Whether this file may be attached.
 *
 * Extension is authoritative; the MIME type only widens the answer, never
 * narrows it. Executables and archives are refused everywhere — neither
 * provider extracts anything from them, so storing one would only produce a
 * file Lucas can never read.
 *
 * @param {{ filename?: string, mimeType?: string }} file
 */
export function isAllowedAttachment(file) {
  const filename = file?.filename;
  if (!filename) return false;

  const extension = extensionOf(filename);
  if (!extension) return false;
  if (ALLOWED_EXTENSIONS.has(extension)) return true;

  const mimeType = String(file?.mimeType || "").toLowerCase();
  if (!mimeType) return false;
  if (ALLOWED_MIME_TYPES.has(mimeType)) return true;
  return ALLOWED_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix));
}

export default isAllowedAttachment;
